// Cyclops Value Calculator - ROI Calculation Engine

import type {
  UseCaseId,
  MissionProfile,
  ChallengeAssessment,
  RecommendedSolution,
} from './discovery-data'
import {
  useCases,
  gpsDenialOptions,
  mitigationOptions,
  getRecommendedLicense,
  getRecommendedMaps,
  getRecommendedSupport,
  getRecommendedIntegration,
  getRecommendedRnd,
  licenseMapCoverage,
} from './discovery-data'
import { getItemById, type QuoteItem } from './pricing-data'

// ============================================================================
// VALUE METRICS
// ============================================================================

export interface ValueMetrics {
  // Mission Success
  currentSuccessRate: number // Percentage
  projectedSuccessRate: number // Percentage with Cyclops
  successRateImprovement: number // Percentage points gained

  // Cost Avoidance
  annualFailedMissions: number
  costPerFailure: number
  annualFailureCost: number
  projectedSavings: number // Annual savings with Cyclops
  
  // Operational Expansion
  newCapabilities: string[]
  additionalMissionTypes: number
  expandedOperationalArea: boolean

  // Fleet Efficiency
  reducedDowntimePercent: number
  fasterDecisionMaking: boolean
  
  // ROI Summary
  totalAnnualValue: number
  paybackPeriodMonths: number
  threeYearROI: number // Percentage
}

// ============================================================================
// CALCULATION ENGINE
// ============================================================================

const CYCLOPS_EFFECTIVENESS = 0.95 // 95% effective at maintaining nav in GPS-denied

export function calculateValueMetrics(
  useCaseId: UseCaseId,
  profile: MissionProfile,
  challenges: ChallengeAssessment,
  totalInvestment: number
): ValueMetrics {
  const useCase = useCases.find((u) => u.id === useCaseId)!
  const gpsDenial = gpsDenialOptions.find((o) => o.value === challenges.gpsDenialFrequency)!
  const mitigation = mitigationOptions.find((o) => o.value === challenges.currentMitigation)!

  // ---- Mission Success Rate ----
  // Current success rate based on abort rate and GPS denial frequency
  const gpsImpactFactor = gpsDenial.riskFactor * (1 - mitigation.effectiveness)
  const currentSuccessRate = Math.max(0, 100 - challenges.missionAbortRate * (1 + gpsImpactFactor))
  
  // Cyclops improvement - closes the gap caused by GPS issues
  const gpsRelatedFailures = challenges.missionAbortRate * gpsImpactFactor
  const cyclopsRecovery = gpsRelatedFailures * CYCLOPS_EFFECTIVENESS
  const projectedSuccessRate = Math.min(100, currentSuccessRate + cyclopsRecovery)
  const successRateImprovement = projectedSuccessRate - currentSuccessRate

  // ---- Cost Avoidance ----
  const annualFailedMissions = Math.round(
    challenges.annualMissions * (challenges.missionAbortRate / 100)
  )
  const gpsRelatedFailedMissions = Math.round(annualFailedMissions * gpsDenial.riskFactor)
  const annualFailureCost = gpsRelatedFailedMissions * challenges.costPerFailedMission
  
  // Projected savings - missions recovered by Cyclops
  const missionsRecovered = Math.round(gpsRelatedFailedMissions * CYCLOPS_EFFECTIVENESS)
  const projectedSavings = missionsRecovered * challenges.costPerFailedMission

  // Apply use case multipliers
  const adjustedSavings = projectedSavings * useCase.valueMultipliers.missionCriticality

  // ---- Operational Expansion ----
  const newCapabilities = getNewCapabilities(useCaseId, challenges.gpsDenialFrequency)
  const expandedOperationalArea = gpsDenial.riskFactor > 0.3

  // ---- Fleet Efficiency ----
  const reducedDowntimePercent = gpsDenial.riskFactor * 15 * CYCLOPS_EFFECTIVENESS

  // ---- ROI Summary ----
  const totalAnnualValue = adjustedSavings + (reducedDowntimePercent * profile.fleetSize * 1000)
  
  const paybackPeriodMonths = totalInvestment > 0 
    ? Math.ceil((totalInvestment / totalAnnualValue) * 12)
    : 0
  
  const threeYearValue = totalAnnualValue * 3
  const threeYearROI = totalInvestment > 0 
    ? Math.round(((threeYearValue - totalInvestment) / totalInvestment) * 100)
    : 0

  return {
    currentSuccessRate: Math.round(currentSuccessRate * 10) / 10,
    projectedSuccessRate: Math.round(projectedSuccessRate * 10) / 10,
    successRateImprovement: Math.round(successRateImprovement * 10) / 10,
    
    annualFailedMissions,
    costPerFailure: challenges.costPerFailedMission,
    annualFailureCost,
    projectedSavings: Math.round(adjustedSavings),
    
    newCapabilities,
    additionalMissionTypes: newCapabilities.length,
    expandedOperationalArea,
    
    reducedDowntimePercent: Math.round(reducedDowntimePercent * 10) / 10,
    fasterDecisionMaking: true,
    
    totalAnnualValue: Math.round(totalAnnualValue),
    paybackPeriodMonths: Math.min(paybackPeriodMonths, 36),
    threeYearROI,
  }
}

function getNewCapabilities(useCaseId: UseCaseId, gpsDenial: string): string[] {
  const base = ['GPS-denied navigation', 'Visual positioning']
  
  const byUseCase: Record<UseCaseId, string[]> = {
    defense: ['Contested environment ops', 'Counter-jamming resilience', 'Covert operations'],
    commercial: ['Urban canyon mapping', 'Indoor-outdoor transitions', 'Infrastructure inspection'],
    logistics: ['Urban delivery routes', 'Near-building navigation', 'Multi-story access'],
    research: ['GPS-free testing', 'Algorithm validation', 'Sensor fusion development'],
  }

  if (gpsDenial === 'never' || gpsDenial === 'occasional') {
    return base
  }

  return [...base, ...byUseCase[useCaseId]]
}

// ============================================================================
// SOLUTION BUILDER
// ============================================================================

export function buildRecommendedSolution(
  useCaseId: UseCaseId,
  profile: MissionProfile
): RecommendedSolution {
  const licenseId = getRecommendedLicense(profile.fleetSize)
  const includedMaps = licenseMapCoverage[licenseId] || 0
  const mapsId = getRecommendedMaps(profile.operationalAreaKm2, includedMaps)
  const supportId = getRecommendedSupport(useCaseId, profile.fleetSize)
  const integrationId = getRecommendedIntegration(profile.cameraRequirement, useCaseId)
  const rndId = getRecommendedRnd(profile.altitudeRequirement, useCaseId)

  return {
    licenseId,
    mapsId,
    supportId,
    integrationId,
    rndId,
    reasoning: {
      license: getLicenseReasoning(profile.fleetSize, licenseId),
      maps: getMapsReasoning(profile.operationalAreaKm2, includedMaps, mapsId),
      support: getSupportReasoning(useCaseId, profile.fleetSize, supportId),
      integration: getIntegrationReasoning(profile.cameraRequirement, integrationId),
      rnd: rndId ? getRndReasoning(profile.altitudeRequirement, rndId) : null,
    },
  }
}

function getLicenseReasoning(fleetSize: number, licenseId: string): string {
  const tierNames: Record<string, string> = {
    'lic-trial': 'Trial',
    'lic-10': 'Starter',
    'lic-100': 'Growth',
    'lic-500': 'Scale',
    'lic-1000': 'Enterprise',
  }
  return `${tierNames[licenseId]} tier covers your ${fleetSize} platform fleet with optimal per-unit pricing.`
}

function getMapsReasoning(needed: number, included: number, mapsId: string | null): string {
  if (!mapsId) {
    return `Your ${needed.toLocaleString()} km² operational area is covered by the included license allocation.`
  }
  const additional = needed - included
  return `Additional ${additional.toLocaleString()} km² needed beyond license inclusion for your operational area.`
}

function getSupportReasoning(_useCaseId: UseCaseId, _fleetSize: number, supportId: string): string {
  if (supportId === 'sup-ent') {
    return 'Enterprise support with dedicated engineer for mission-critical operations.'
  }
  if (supportId === 'sup-prem') {
    return 'Premium support with 48-hour SLA and direct engineering access.'
  }
  return 'Standard support with documentation and email access included.'
}

function getIntegrationReasoning(_camera: string, integrationId: string): string {
  if (integrationId === 'int-self') {
    return 'Self-service integration - FLIR Boson Plus compatible out of the box.'
  }
  if (integrationId === 'int-key') {
    return 'Turnkey integration for complete solution delivery with your camera system.'
  }
  return 'Supported integration with Theseus engineering guidance for your camera setup.'
}

function getRndReasoning(altitude: string, rndId: string): string {
  const altitudeLabel = altitude === 'high_6km' ? '6km' : '2km'
  const led = rndId.includes('-the') ? 'Theseus-led' : 'Customer-led'
  return `${altitudeLabel} altitude ceiling development with ${led} flight testing and validation.`
}

// ============================================================================
// QUOTE BUILDER
// ============================================================================

export function buildQuoteFromSolution(solution: RecommendedSolution): QuoteItem[] {
  const items: QuoteItem[] = []

  // License
  const licenseItem = getItemById(solution.licenseId)
  if (licenseItem) {
    items.push({ item: licenseItem, categoryId: 'licenses', quantity: 1 })
  }

  // Maps (if additional needed)
  if (solution.mapsId) {
    const mapsItem = getItemById(solution.mapsId)
    if (mapsItem) {
      items.push({ item: mapsItem, categoryId: 'maps', quantity: 1 })
    }
  }

  // Support (if not standard)
  if (solution.supportId !== 'sup-std') {
    const supportItem = getItemById(solution.supportId)
    if (supportItem) {
      items.push({ item: supportItem, categoryId: 'support', quantity: 1 })
    }
  }

  // Integration (if not self-service)
  if (solution.integrationId !== 'int-self') {
    const integrationItem = getItemById(solution.integrationId)
    if (integrationItem) {
      items.push({ item: integrationItem, categoryId: 'integration', quantity: 1 })
    }
  }

  // R&D (if altitude requirements)
  if (solution.rndId) {
    const rndItem = getItemById(solution.rndId)
    if (rndItem) {
      items.push({ item: rndItem, categoryId: 'rnd', quantity: 1 })
    }
  }

  return items
}

export function calculateTotalInvestment(items: QuoteItem[]): number {
  return items.reduce((sum, qi) => sum + qi.item.price * qi.quantity, 0)
}

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

export function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`
  }
  return `$${value.toLocaleString()}`
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

export function formatPayback(months: number): string {
  if (months <= 0) return 'Immediate'
  if (months === 1) return '1 month'
  if (months < 12) return `${months} months`
  const years = Math.floor(months / 12)
  const remainingMonths = months % 12
  if (remainingMonths === 0) {
    return years === 1 ? '1 year' : `${years} years`
  }
  return `${years}y ${remainingMonths}mo`
}

